import { useEffect } from "react";
import {
  evaluatePendingVersionSkewReload,
  reportLivenessBuild,
} from "@/lib/version-skew";
import { reportResilienceState } from "./resilience-store";

// QB-NET-PROBE heartbeat hits the v2 liveness endpoint (declared in
// backend/src/quartel/api/routes/health.py). The bare `/api/v2/health`
// path is the router prefix, not a handler — it 404s, which used to
// spam dev logs every 15s per open tab.
const HEALTH_URL = "/api/v2/health/liveness";
const HEALTH_INTERVAL_MS = 15_000;
const SLOW_HEALTH_MS = 2_000;

export function useNetworkStatus(): void {
  useEffect(() => {
    const controller = new AbortController();
    const probe = (): void => {
      void probeHealth(controller.signal);
    };
    window.addEventListener("online", probe);
    window.addEventListener("offline", reportOffline);
    document.addEventListener("visibilitychange", reEvaluateSkewOnVisible);
    probe();
    const timer = setInterval(probe, HEALTH_INTERVAL_MS);
    return () => cleanupNetworkStatus(controller, timer, probe);
  }, []);
}

async function probeHealth(signal: AbortSignal): Promise<void> {
  if (navigator.onLine === false) {
    reportOffline();
    return;
  }
  const start = performance.now();
  try {
    const res = await fetch(HEALTH_URL, { cache: "no-store", signal });
    reportHealthResult(res.ok, performance.now() - start);
    // Liveness carries the backend image build id — feed the version-skew
    // monitor so an open tab notices a deploy and safely reloads when idle.
    if (res.ok) reportLivenessBuild(await readLivenessBuild(res));
  } catch {
    reportResilienceState("reconnecting", "QB-NET-PROBE");
  }
}

async function readLivenessBuild(res: Response): Promise<unknown> {
  try {
    const body = (await res.json()) as Record<string, unknown> | null;
    return body?.["build"];
  } catch {
    // Body parsing is best-effort: older backends (and test fakes) answer
    // liveness without a JSON body — never let it poison the health probe.
    return undefined;
  }
}

// A skew reload deferred while the tab was hidden fires as soon as the
// operator comes back — without waiting up to 15s for the next poll.
function reEvaluateSkewOnVisible(): void {
  if (document.visibilityState !== "visible") return;
  evaluatePendingVersionSkewReload();
}

function reportHealthResult(ok: boolean, elapsedMs: number): void {
  if (!ok) {
    reportResilienceState("reconnecting", "QB-SRV-HEALTH");
    return;
  }
  reportResilienceState(elapsedMs > SLOW_HEALTH_MS ? "slow" : "online");
}

function reportOffline(): void {
  reportResilienceState("offline", "QB-NET-OFFLINE");
}

function cleanupNetworkStatus(
  controller: AbortController,
  timer: ReturnType<typeof setInterval>,
  probe: () => void,
): void {
  controller.abort();
  clearInterval(timer);
  window.removeEventListener("online", probe);
  window.removeEventListener("offline", reportOffline);
  document.removeEventListener("visibilitychange", reEvaluateSkewOnVisible);
}
